import React, { useCallback } from 'react';
import { FollowCard } from '../follows/FollowCard';
import { StatisticContainer } from '../StatisticContainer';
import { FollowersIcon } from 'components/svg/dashboard/FollowersIcon';
import {
    Espin_Role_Enum,
    FollowCollegeDocument,
    FollowCollegeMutation,
    FollowCollegeMutationVariables,
    ParentFollowsDocument,
    ParentFollowsMutation,
    ParentFollowsMutationVariables,
    PlayerFollowersFragment,
    UnfollowCollegeDocument,
    UnfollowCollegeMutation,
    UnfollowCollegeMutationVariables,
} from '../../../generated/spin-graphql';
import { gql } from '@urql/core';
import { useAuthenticatedMutation } from '../../../client/hooks';
import { useAppState } from '../../../client/providers/AppProvider';

type Props = {
    closeColleges: PlayerFollowersFragment[];
    farColleges: PlayerFollowersFragment[];
    totalFollowers: number;
    newFollowers: number;
    selectedPlayer?: number;
    listOfFollows: string[];
};

gql`
    fragment PlayerFollowers on espin_follow {
        ...FollowCard
    }
`;

gql`
    mutation FollowCollege($collegeId: uuid!) {
        insert_espin_follow_one(object: { college_id: $collegeId }) {
            id
        }
    }
`;

gql`
    mutation UnfollowCollege($playerUserId: String, $collegeId: uuid!) {
        delete_espin_follow(
            where: {
                player_user_id: { _eq: $playerUserId }
                follow_type: { _eq: player_follows_college }
                college_id: { _eq: $collegeId }
            }
        ) {
            returning {
                id
            }
        }
    }
`;

export const PlayerFollowers: React.FC<Props> = ({
    closeColleges,
    farColleges,
    totalFollowers,
    newFollowers,
    selectedPlayer,
    listOfFollows,
}) => {
    const { user } = useAppState();
    // player follow a college
    const [_, unFollowCollege] = useAuthenticatedMutation<UnfollowCollegeMutation, UnfollowCollegeMutationVariables>(
        UnfollowCollegeDocument,
    );

    const [__, followCollege] = useAuthenticatedMutation<FollowCollegeMutation, FollowCollegeMutationVariables>(
        FollowCollegeDocument,
    );
    const [___, parentFollowCollege] = useAuthenticatedMutation<ParentFollowsMutation, ParentFollowsMutationVariables>(
        ParentFollowsDocument,
    );

    const handleFollowButtonClicked = useCallback(
        (collegeId: string, following: boolean) => {
            // could filter here, but the component below needs list of follows to properly render it too.

            if (following) {
                // is dis a parent?
                if (user.role === Espin_Role_Enum.EspinParentUser) {
                    const playerId = user.parent_profile.child_profiles[0].user_id;
                    return unFollowCollege(
                        { collegeId, playerUserId: playerId },
                        { additionalTypenames: ['espin_follow'] },
                    );
                }
                unFollowCollege({ collegeId, playerUserId: user?.id }, { additionalTypenames: ['espin_follow'] });
            } else {
                if (user.role === Espin_Role_Enum.EspinParentUser) {
                    const playerId = user.parent_profile.child_profiles[0].user_id;
                    return parentFollowCollege(
                        { input: { college_id: collegeId, player_user_id: playerId } },
                        { additionalTypenames: ['espin_follow'] },
                    );
                }
                followCollege({ collegeId }, { additionalTypenames: ['espin_follow'] });
            }
        },
        [
            followCollege,
            parentFollowCollege,
            unFollowCollege,
            user?.id,
            user?.parent_profile?.child_profiles,
            user?.role,
        ],
    );

    return (
        <div className="follow-container">
            <StatisticContainer label={'Total Followers'} icon={<FollowersIcon />} value={`${totalFollowers}`} />
            <StatisticContainer label={'New Followers'} icon={<FollowersIcon />} value={`${newFollowers}`} />
            <FollowCard
                playerCollegesFollowing={listOfFollows}
                className="player-following-card"
                title="Colleges within 100 miles"
                unsubbedTitle="Unlock Followers"
                unsubbedSubtitle="Upgrade to Premium to see your followers!"
                fakeDataSubtitle="Upgrade to Premium to see your followers!"
                followingList={closeColleges}
                totalFollowing={closeColleges.length}
                noContentIcon={<FollowersIcon />}
                noContentLabel={'You have no followers'}
                selectedChild={selectedPlayer}
                handleFollowButtonClicked={(collegeId, following) => handleFollowButtonClicked(collegeId, following)}
            />
            <FollowCard
                playerCollegesFollowing={listOfFollows}
                className="player-following-card"
                title="Colleges further than 100 miles"
                unsubbedTitle="Unlock Followers"
                unsubbedSubtitle="Upgrade to Premium to see your followers!"
                fakeDataSubtitle="Upgrade to Premium to see your followers!"
                followingList={farColleges}
                totalFollowing={farColleges.length}
                noContentIcon={<FollowersIcon />}
                noContentLabel={'You have no followers'}
                selectedChild={selectedPlayer}
                handleFollowButtonClicked={(collegeId, following) => handleFollowButtonClicked(collegeId, following)}
            />
        </div>
    );
};
